/**
 * @module msysFS
 * @description
 * Mosy File System library
 * Provides file system related methods unified for all supported build platforms
 * (web, node, NWjs, Capacitor).
 *
 * Example of use:
 * const mfs = require('msysFS')
 * mfs.readFile("data/map/sk.txt", function(err, text){ ... })
 * mfs.readFile("c:\\windows\windows.txt", function(err, json){ ... })
 * mfs.readFileJSON("$run/mosy_client.cfg", function(err, json){ ... })
 */

let cfs; let app_root = ''; let data_root = ''; let device_id = ''
const _ = require('lodash')
const mime_magic = require('./mime_magic')
let ext2mime
try { ext2mime = require('mime-db-loader!mime-db/db.json') } catch (e) { ext2mime = {} }
const nfs = require('fs')
const mkpath = require('mkpath')
const path = require('path')
const http = require('http')
const https = require('https')
const child_process = require('child_process')
const base64 = require('js-base64').Base64
const mcrypt = require('../msysCrypt')
const { Capacitor } = require('@capacitor/core')
const { Device } = require('@capacitor/device')
const { Hw } = require('@soitron/coppernic-hw')
const os = require('os')

const { Filesystem, Directory, Encoding } = require('@capacitor/filesystem')

const DEBUG_PATHS = 0

const HTTP_TIMEOUT = 10000
const mfs = module.exports = { promises: {} }

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* returns execution platform
* @returns web,capacitor,nwjs,node or unknown
*/
mfs.platform = function () {
  return (
    Capacitor.isNativePlatform() ? 'capacitor' :
      typeof (window) === 'object' && (typeof window.nw !== 'undefined') ? 'nwjs' :
        typeof (process) === 'object' && process.argv.length > 0 ? 'node' :
          typeof (window) === 'object' ? 'web' :
            'unknown'
  )
} // platform

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* platform-dependent initialization of msysFS
*/
mfs.init = function (custom_app_root) {
  const platform = mfs.platform()
  if(DEBUG_PATHS) console.info('platform: ', platform)
  let exec_root
  if(platform === 'node' || platform === 'nwjs') {
    if(!custom_app_root && process.env.APP_ROOT) {
      custom_app_root = process.env.APP_ROOT
    }
    if(custom_app_root) {
      app_root = custom_app_root.replace(/\\/g, '/').replace(/^(\w:\/)\//, '$1').replace(/\/$/, '')
      exec_root = app_root
    }else if(platform === 'node') {
      if(DEBUG_PATHS) console.log('process.argv[1]', process.argv[1])
      if(DEBUG_PATHS) console.log('process.cwd()', process.cwd())
      if(DEBUG_PATHS) console.log('process.env.PWD', process.env.PWD)
      if(DEBUG_PATHS) console.log('process.env.CD', process.env.CD)
      if(DEBUG_PATHS) console.log('process.execPath', process.execPath)
      if(DEBUG_PATHS) console.log('__dirname', __dirname)
      if(DEBUG_PATHS) console.log('__filename', __filename)
      if(DEBUG_PATHS) console.log('require.main.filename', require.main.filename)
      app_root = __dirname
      exec_root = process.cwd().replace(/\\/g, '/').replace(/\/[^/]+$/, '').replace(/\/lib\/node-webkit.*$/, '')
    }else {
      // NWjs can be run either as nw.exe <mosy-app-package-or-zip> or as mosy-app.exe
      if(DEBUG_PATHS) console.log('process.argv[1]', process.argv[1])
      if(DEBUG_PATHS) console.log('process.cwd()', process.cwd())
      if(DEBUG_PATHS) console.log('process.env.PWD', process.env.PWD)
      if(DEBUG_PATHS) console.log('process.env.CD', process.env.CD)
      if(DEBUG_PATHS) console.log('process.execPath', process.execPath)
      if(DEBUG_PATHS) console.log('__dirname', __dirname)
      if(DEBUG_PATHS) console.log('nw.process.cwd()', nw.process.cwd())
      if(DEBUG_PATHS) console.log('nw.process.execPath', nw.process.execPath)
      if(DEBUG_PATHS) console.log('location.href', location.href)
      app_root = exec_root = process.execPath.replace(/\\/g, '/').replace(/\/[^/]+$/, '').replace(/\/lib\/node-webkit.*$/, '')
    }

    if(DEBUG_PATHS) console.log('app_root', app_root)
    if(DEBUG_PATHS) console.log('exec_root', exec_root)

    data_root = exec_root
    if(app_root.match(/^\/private\/var/)) {
      // special treatment on Mac development machines - use MOSY_SRC env variable or user's HOME as data_root
      data_root = platform === 'node' ? process.env.HOME : nw.process.env.HOME
      try {
        let profile = '' + nfs.readFileSync(data_root + '/.bash_profile')
        profile = profile.replace(/^#.*/mg, '')
        const x = profile.match(/^\s*export\s+MOSY_SRC=(.*)/m)
        data_root = x[1].replace(/\/$/, '') + '/run/' + MOSY_CUSTOMER + '/' + MOSY_APP + MOSY_HOST
      } catch (e) {
        // console.error(e);
      }
    }else if(app_root.match(/AppData\/Local\/Temp/)) {
      data_root = exec_root
    }else if(app_root.match(/^\/snapshot\//)) {
      app_root = data_root = process.execPath.replace(/\\/g, '/').replace(/\/[^/]+$/, '') // app packaged by pkg
    }else if(exec_root.indexOf('mac/nwjs/nwjs.app') > 0) {
      data_root = app_root.replace(/\/build\/(\w+)\/(\w+)\/www/, '/run/$1/$2')
    }else {
      data_root = app_root
    }

    app_root = app_root.replace(/\/$/, '')
    data_root = data_root.replace(/\/$/, '')

    if(global.mfs_data_dir) {
      data_root = path.resolve(global.mfs_data_dir).replace(/[\\/]$/, '')
    }

    if(DEBUG_PATHS) console.log('app_root', app_root)
    if(DEBUG_PATHS) console.log('data_root', data_root)

    try {
      mfs.mkpathSync(MOSY_APP + MOSY_HOST + '.store') // create directory for storing localStorage file replacement
    } catch (e) {
      // console.error(e);

    }
  }else if(platform === 'capacitor') {
    cfs = Filesystem
    const getPermissions = async () => {
      await mfs.requestPermission(async ()=>{
        try {
          const permissions = await Hw.checkPermissions()
          if(permissions !== 'granted') {
            try {
              await Hw.requestPermissions()
            } catch (error) {
              console.error('Hw.requestPermissions failed: '+error)
            }
          }
        } catch (error) {
          console.error('Hw.checkPermissions failed: '+error)
        }
      })
    }
    getPermissions()
  }else if(platform === 'web') {
    data_root = app_root = window.location.href.replace(/[^/]+$/, '').replace(/\/$/, '')
  }
} // init

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* return current operating system
* @returns win,mac,unix,ios,android or unknown
*/
mfs.os = function () {
  const platform = mfs.platform()
  if(platform === 'nwjs') {
    const os = _.toString(nw.process.platform)
    return (os === 'darwin' ? 'mac' : os === 'win32' ? 'win' : 'unknown')
  }else if(platform === 'node') {
    const os = _.toString(process.platform)
    return (os === 'darwin' ? 'mac' : os === 'win32' ? 'win' : 'unknown')
  }else {
    const agent =
      Capacitor.isNativePlatform() ? Capacitor.getPlatform() : // Capacitor native platform info
        (typeof (navigator) === 'object' && typeof (navigator.userAgent) === 'string') ? navigator.userAgent : // web browser/view agent info
          ''

    return (
      /ios|iphone|ipod|ipad/i.test(agent) ? 'ios' :
        /android/i.test(agent) ? 'android' :
          /Macintosh/i.test(agent) ? 'mac' :
            /Windows/i.test(agent) ? 'win' :
              'unknown'
    )
  }
} // os

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* returns type of device where the application is running
* @returns tablet,phone,pc,unknown
*/
mfs.device = function () {
  if(mfs.platform() === 'nwjs') { return 'pc' }
  if(typeof (navigator) !== 'object') { return 'unknown' }
  if(typeof (navigator.userAgent) !== 'string') { return 'unknown' }

  return (
    /iphone|ipod/i.test(navigator.userAgent) ? 'phone' :
      /mobile/i.test(navigator.userAgent) && /android/i.test(navigator.userAgent) ? 'phone' :
        /windows\s+phone/i.test(navigator.userAgent) ? 'phone' :
          /ipad/i.test(navigator.userAgent) ? 'tablet' :
            /android/i.test(navigator.userAgent) ? 'tablet' :
              'pc'
  )
} // device

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* returns type of device where the application is running
* @returns tablet,phone,pc,unknown
*/
mfs.hostname = function () {
  let hostname = ''
  const platform = mfs.platform()
  if(/node|nwjs/.test(platform)) {
    hostname = require('os').hostname()
  } else if (platform === 'web') {
    hostname = window.location.hostname
  } else if (platform === 'capacitor') {
    hostname = device_id
  }
  return hostname
} // hostname

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
  * @description
  * Obtains host device serial number
  */
mfs.getDeviceId = async function () {
  if(device_id) return device_id
  const platform = mfs.platform()
  device_id = String(platform??'')
  if(platform === 'capacitor') {
    for(let i=0; i<10; i++) {
      if(Device && Device.getInfo) {
        try{
          const device = await Device.getInfo()
          if(device.serial) {
            device_id = device.serial
            return device_id
          }
        }catch(e) {
          console.error('getDeviceId: Capacitor Device.getInfo failed: '+e)
        }
        try {
          const { serial } = await Hw.getSerialNumber()
          if(serial) {
            device_id = serial
            return device_id
          }
        } catch (error) {
          console.error('getDeviceId: Hw.getSerialNumber failed: '+error)
        }
      }
      await asyncTimeout(500)
    }
    console.error('getDeviceId: Capacitor Device.getInfo not available')
  }else if(platform === 'nwjs' || platform === 'node') {
    try{
      const reMac = /^([a-zA-Z0-9]{2}[:-]){5}[a-zA-Z0-9]{2}$/
      const ifaces = os.networkInterfaces()

      const macs = {}
      for(const name in ifaces) {
        if (!/^(wwan|eth|en|ethernet|Local Area Connection)/i.test(name)) continue

        const list = ifaces[name]
        if(!Array.isArray(list)) continue
        for(const item of list) {
          if(item?.internal) continue
          let mac = String(item?.mac??'')
          if(reMac.test(mac)) {
            mac = mac.replace(/[:-]/g, '').toUpperCase()
            if(mac !== '000000000000') {
              macs[name] = mac
              break // all interface items shall have the same MAC address
            }
          }
        }
      }

      if(macs.eth0) {
        device_id = macs.eth0
      }else if(macs.en0) {
        device_id = macs.en0
      }else {
        const names = Object.keys(macs).sort()
        if(names.length>0) {
          device_id = macs[names[0]]
        }else {
          console.error('getDeviceId: no non-zero MAC address found')
        }
      }
    }catch(e) {
      console.error('getDeviceId: networkInterfaces() error: '+e)
    }
  }else if(platform === 'web') {
    console.warn('getDeviceId: cannot determine device ID on web platform: ')
  }else {
    console.error('getDeviceId: unsupported platform: '+platform)
  }
  return device_id
} // getDeviceId

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* returns OS login name (on Windows in format domain\username)
*/
mfs.login = function () {
  if(!child_process) return ''
  return (('' + child_process.execSync('whoami')).replace(/\r?\n/, ''))
} // login

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Returns hash with program startup arguments
* @returns {object} - hash with param:value pairs
* WEB: params are taken from search part of location URL, e.g. ?param1=value1&param2=value2
* NODE, NW: params are taken from command line, e.g. nwjs build/client/www param1 value1 param2 value2
* CAPACITOR: startup arguments are not available
*/
mfs.args = function () {
  const platform = mfs.platform()
  let args = []
  if(platform === 'web' && window && window.location) {
    location.search.slice(1).split('&').forEach(function (item) {
      const pair = item.match(/^([^=]+)(=(.*))?$/)
      if(pair != null) { args.push(pair[1], pair[2]) }
    })
  }else if(platform === 'node') {
    args = process.argv.slice(2)
  }else if(platform === 'nwjs') {
    args = nw.App.argv
  }

  const hash = {}
  for (let i = 0; i < args.length; i += 2) {
    const key = args[i].replace(/^--/, '')
    let val = args[i + 1]
    if(typeof (val) !== 'string') { val = '' }
    hash[key] = val
  }
  return hash
} // args

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* convert Base64 encoded data to BLOB
*/
mfs.base64ToBlob = function (b64Data, contentType) {
  b64Data = _.toString(b64Data).replace(/^.*?,/, '')
  const sliceSize = 512
  const byteCharacters = atob(b64Data)
  const byteArrays = []
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: _.toString(contentType) })
  return blob
} // base64ToBlob

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* convert Base64 encoded data to UTF8 string
*/
mfs.base64ToUTF8 = function (b64Data) {
  b64Data = _.toString(b64Data).replace(/^.*?,/, '')
  if(nfs) {
    return Buffer.from(b64Data, 'base64').toString('utf8')
  }else {
    return base64.decode(b64Data)
  }
} // base64ToUTF8

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Returns full path resp. URL from given path depending on running platform.
* @param {string} rel_path
* For absolute path returns the given absolute path.
* For paths beginning with $app/ returns absolute path relative to the path where the application is stored.
* For relative path (e.g. map/sk/file.txt) returns absolute path relative to data root:
* - for Cordova, this is cordova.file.dataDirectory
* - for NWjs this is directory where the application executable is stored
* - for web application this is URL where application start script is located
* - for Node build this is path where application startup script is stored
*/
mfs.fullpath = function (rel_path) {
  rel_path = _.toString(rel_path).replace(/\\/g, '/')
  const platform = mfs.platform()
  if(platform === 'web') {
    if(/^https?:/.test(rel_path)) {
      return rel_path
    }else if(/^\//.test(rel_path)) {
      return window.location.href.replace(/^(\w+:\/\/[^/]+).*/, '$1') + rel_path
    }else if(/^\$app\//.test(rel_path)) {
      return app_root + '/' + rel_path
    }else {
      return data_root + '/' + rel_path
    }
  }
  if((platform === 'node' || platform === 'nwjs') && /^~[\\/]/.test(rel_path)) {
    try {
      const homedir = require('os').homedir()
      rel_path = rel_path.replace(/^~/, homedir)
      rel_path = rel_path.replace(/\\/g, '/')
    } catch (e) {
      // console.error(e)
    }
  }

  if(/\/\/|^\w:[/\\]|^\//.test(rel_path)) return _.toString(rel_path)

  // /build/ -> /run/ is a special tweak for testing; if we run from build dir, remove build/CUSTOMER/APP prefix
  // if(mfs.platform() == 'node' && /node$|node.exe$/.test(process.argv[0]))
  // rel_path = /^\$app\//.test(rel_path) ? rel_path.replace('$app/', app_root + '/') : (data_root == '' ? rel_path : (data_root + '/' + rel_path)).replace('/build/', '/run/');
  if(/^\$app\//.test(rel_path)) {
    rel_path = rel_path.replace('$app/', app_root + '/')
  }else if(data_root !== '') {
    const data_root_parts = data_root.split(/[\\/]+/).map(part => part === 'build' ? 'run' : part)
    const rel_path_parts = rel_path.split(/[\\/]+/)
    rel_path = [...data_root_parts, ...rel_path_parts].join(path.sep)
  }

  // if(process.platform == 'win32') {
  //   const parts = rel_path.split(/[\\/]+/);
  //   rel_path = path.win32.join(...parts);
  // }
  // another special tweak to dinf ffmpeg during development
  rel_path = rel_path.replace(/\/build\/\w+\/\w+\/\.\.\/system\/ffmpeg/, '/extra/mac/ffmpeg/ffmpeg')
  rel_path = rel_path.replace(/\/build\/\w+\/\w+\/ffmpeg/, '/extra/mac/ffmpeg/ffmpeg')

  if(platform === 'node') rel_path = path.resolve(rel_path)

  return rel_path
} // fullpath

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Returns parent directory of given file/dir
* @param {string} rel_path
*/
mfs.dir = function (rel_path) {
  return _.toString(rel_path).replace(/\\/g, '/').replace(/[^/]+$/, '').replace(/\/$/, '')
} // dir

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Returns path to root folder + relative path depending on running platform.
* @param {string} rel_path
* For absolute path returns ['/',relative_path]
* For paths beginning with $app/ returns [app_root_dir, relative_path]
* For relative path (e.g. map/sk/file.txt) returns [data_root_dir, relative_path]
* - for Cordova, this is cordova.file.dataDirectory
* - for NWjs this is directory where the application executable is stored
* - for web application this is URL where application start script is located
* - for Node build this is path where application startup script is stored
*/
mfs.splitpath = function (rel_path) {
  rel_path = _.toString(rel_path).replace(/\\/g, '/')
  const platform = mfs.platform()
  if(platform === 'web' && /^\//.test(rel_path)) { return ([window.location.href.replace(/^(\w+:\/\/[^/]+).*/, '$1') + '/', rel_path.replace(/^\//, '')]) }
  if((platform === 'node' || platform === 'nwjs') && /^~[\\/]/.test(rel_path)) {
    try {
      const homedir = require('os').homedir()
      rel_path = rel_path.replace(/^~/, homedir)
      rel_path = rel_path.replace(/\\/g, '/')
    } catch (e) {
      // console.error(e)
    }
  }
  const x = rel_path.match(/^(\w:[/\\]|\/)(.*)/)
  if(x) {
    return ([x[1], x[2]])
  }

  // /build/ -> /run/ is a special tweak for testing; if we run from build dir, remove build/CUSTOMER/APP prefix
  if(/^\$app\//.test(rel_path)) {
    return ([app_root + '/', rel_path.replace(/^\$app\//, '')])
  }else {
    data_root = data_root.replace('/build/', '/run/')
    return ([data_root + '/', rel_path])
  }
} // splitpath

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Checks if the file exists; only for NODE environment
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
*/
mfs.existsFile = function (file_path) {
  file_path = mfs.fullpath(file_path)
  if(nfs) {
    return nfs.existsSync(file_path)
  }else {
    return false
  }
} // existsFile

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Returns size and last modified datetime for given file
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {function} callback(err, stats) - called back when info is retrieved
*/
mfs.statFile = async function (file_path, callback) {
  if(!file_path) return callback('Not found')
  file_path = mfs.fullpath(file_path)
  if(/^https?:/.test(file_path)) {
    return callback('Method not supported')
  }
  if(nfs) {
    nfs.stat(file_path, (err, stats) => {
      if(err || !stats) return callback(err)
      stats = {
        size: stats.isDirectory() ? -1 : stats.size,
        mtime: _.natural(stats.mtime.valueOf() / 1000),
      }
      return callback(null, stats)
    })
  }else if(Capacitor.isNativePlatform()) {
    try {
      const opt =
        /:\/\//.test(file_path) ? { path: file_path } :
          /^\//.test(file_path) ? { path: file_path, directory: Directory.Documents } :
            { path: file_path, directory: Directory.Data }
      const stat = await Filesystem.stat(opt)
      callback(null, {
        size: stat.type === 'file' ? _.natural(stat.size) : -1,
        mtime: _.natural(stat.mtime / 1000),
      })
    } catch (e) {
      callback(String(e))
    }
  }else {
    callback('Platform not supported')
  }
} // statFile

// ////////////////////////////////////////////////////////////////////////////////////////////////////
// /**
// * Reads content of the file from its path relative to application file
// * @param {string} file_path - logical (prefixed) path (see prefixes in module description)
// * @param {string} type - type of result: 'text' (default), 'binary', 'dataurl', 'base64'
// * @param {function} callback(err, data) - called back when finished reading
// */

// mfs.readFile = async function (file_path, file_options, callback) {
//   const platform = mfs.platform()
//   if(typeof (file_options) === 'function') {
//     callback = file_options
//     file_options = 'text'
//   }
//   if(!file_options) file_options = 'text'
//   if(typeof file_options !== 'object') file_options = { type: _.toString(file_options) || 'text' }
//   let type = file_options.type
//   if(!callback) callback = function () { }
//   type = /^(text|binary|dataurl|base64)$/.test(type) ? type : 'text'
//   file_path = mfs.fullpath(file_path)

//   if(/^https?:/.test(file_path)) {
//     if(http) {
//       // NODE/NWJS supports HTTP module
//       let timeouter
//       let request = null
//       timeouter = setTimeout(() => {
//         timeouter = null
//         const cb = callback
//         callback = function () { }
//         if(request) {
//           try { request.abort() } catch (e) { }
//         }
//         cb('timeout')
//       }, HTTP_TIMEOUT)

//       const digest = /(.*):DIGEST=(.+):(.*)$/.exec(file_path)
//       const url = digest ? digest[1] : file_path
//       const parsed_url = url.match(/^(https?:)\/\/(?:([^:]+):([^@]*)@)?(([^/:]+)(?::(\d+))?)(.*)/)
//       if(!parsed_url) return callback('Invalid URL')
//       const [, protocol, username, password, hostport, host, port, uri] = parsed_url
//       const options = {
//         protocol,
//         method: file_options.method || 'GET',
//         host,
//         port,
//         path: uri,
//         headers: {},
//       }
//       let postData = null
//       if(_.isObjectLike(file_options.http_options)) {
//         for (const p in file_options.http_options) {
//           if(p === 'body') postData = file_options.http_options[p]
//           else options[p] = file_options.http_options[p]
//         }
//       }
//       if(!_.isObjectLike(options.headers)) options.headers = {}
//       if(/nwjs|node/.test(platform)) {
//         options.headers['User-Agent'] = 'Mosy mPatrol 1.0'
//       }
//       if(postData !== null) { options.headers['Content-Length'] = postData.length }

//       if(digest) {
//         const realm = digest[2]
//         const nonce = digest[3]
//         const ha1 = mcrypt.md5(`${username}:${realm}:${password}`)
//         const ha2 = mcrypt.md5(`${options.method}:${uri}`)
//         const response = mcrypt.md5(`${ha1}:${nonce}:${ha2}`)
//         options.headers.Authorization = `Digest username="${username}", realm="${realm}", nonce="${nonce}", uri="${uri}", response="${response}"`
//       }

//       if(file_options.mtime > 0) { options.headers['If-Modified-Since'] = new Date(file_options.mtime * 1000).toUTCString() }

//       request = (protocol === 'https:' ? https : http).request(options, (res) => {
//         const contentType = res.headers['content-type'] || 'application/octet-stream'
//         const chunks = []
//         let finished = false
//         res.on('data', chunk => chunks.push(chunk))
//         res.on('end', () => {
//           if(finished) return
//           finished = true
//           if(timeouter) {
//             clearTimeout(timeouter)
//             timeouter = null
//           }
//           if(res.statusCode === 304) return callback()
//           if(res.statusCode === 401 && typeof (file_path) === 'string') {
//             if(digest) return callback(res.statusCode) // perhaps invalid password

//             const auth = /realm="([^"]*)",.*nonce="([^"]*)"/.exec(res.headers['www-authenticate'])
//             if(auth) { return mfs.readFile(file_path + ':DIGEST=' + auth[1] + ':' + auth[2], file_options, callback) }else { return callback(res.statusCode) }
//           }else if(res.statusCode !== 200) {
//             return callback(res.statusCode)
//           }
//           const last_modified = parseInt((new Date(res.headers['last-modified']).valueOf() || Date.now()) / 1000)
//           if(file_options.mtime > 0 && last_modified > 0 && file_options.mtime > last_modified) return callback()
//           const data = Buffer.concat(chunks)
//           if(type === 'binary') { callback(null, data, last_modified) }else if(type === 'dataurl') { callback(null, 'data:' + contentType + ';base64,' + data.toString('base64'), last_modified) }else { callback(null, data.toString('utf8'), last_modified) }
//         })
//         res.on('close', () => {
//           if(finished) return
//           finished = true
//           if(timeouter) {
//             clearTimeout(timeouter)
//             timeouter = null
//           }
//           callback('CONNECTION CLOSED')
//         })
//         res.on('error', err => {
//           if(finished) return
//           finished = true
//           if(timeouter) {
//             clearTimeout(timeouter)
//             timeouter = null
//           }
//           callback(err.message)
//         })
//       })
//       request.on('error', err => {
//         if(timeouter) {
//           clearTimeout(timeouter)
//           timeouter = null
//         }
//         callback(err.message)
//       })
//       if(postData !== null) request.write(postData)
//       request.end()
//     }else {
//       // BROWSER/CORDOVA does not support HTTP module, fallback using XMLHttpRequest
//       const req = new XMLHttpRequest()
//       req.onreadystatechange = function () {
//         if(req.readyState !== XMLHttpRequest.DONE) return
//         if(req.status === 200) {
//           const contentType = req.getResponseHeader('content-type') || 'application/octet-stream'
//           if(type === 'binary') {
//             // no binary form in browser environments
//             callback(null, req.response)
//           }else if(type === 'dataurl') {
//             const bytes = new Uint8Array(req.response)
//             const binary = [].map.call(bytes, (byte) => String.fromCharCode(byte)).join('')
//             const b64 = btoa(binary)
//             callback(null, 'data:' + contentType + ';base64,' + b64)
//           }else {
//             callback(null, req.responseText)
//           }
//         }else {
//           callback(req.status)
//         }
//       }
//       req.open('GET', file_path)
//       req.responseType = (type === 'binary' || type === 'dataurl') ? 'arraybuffer' : 'text'
//       req.send()
//     }
//   }else if(Capacitor.isNativePlatform()) {
//     console.log('Capacitor.isNativePlatform()', file_path)
//     try {
//       const opt =
//         /:\/\//.test(file_path) ? { path: file_path } :
//           /^\//.test(file_path) ? { path: file_path, directory: Directory.Documents } :
//             { path: file_path, directory: Directory.Data }
//       if(type === 'text') opt.encoding = Encoding.UTF8
//       let { data } = await Filesystem.readFile(opt)
//       if(type === 'binary') {
//         data = Uint8Array.from(atob(data), c => c.charCodeAt(0))
//       }else if(type === 'dataurl') {
//         const mime_type = ext2mime[file_path.replace(/.*\./, '')] || 'application/octet-stream'
//         data = 'data:' + mime_type + ';base64,' + data
//       }
//       callback(null, data)
//     } catch (e) {
//       callback(String(e))
//     }
//   }else if(nfs) {
//     const options =
//       type === 'binary' ? {} :
//         type === 'dataurl' ? { encoding: 'base64' } :
//           type === 'base64' ? { encoding: 'base64' } :
//             { encoding: 'utf8' }
//     const mime_type = type === 'dataurl' ? (ext2mime[file_path.replace(/.*\./, '')] || 'application/octet-stream') : ''
//     nfs.readFile(
//       file_path,
//       options,
//       function (err, data) {
//         if(err) { callback(err.message, null) }else { callback(null, type === 'dataurl' ? 'data:' + mime_type + ';base64,' + data : data) }
//       },
//     )
//   }else {
//     callback('msysFS: cannot read file', null)
//   }
// }

mfs.readFile = async function (filePath, fileOptions, callback) {
  const platform = mfs.platform()

  // Handle optional file options and callback
  if (typeof fileOptions === 'function') {
    callback = fileOptions
    fileOptions = 'text'
  }
  fileOptions = fileOptions || 'text'
  if (typeof fileOptions !== 'object') {
    fileOptions = { type: _.toString(fileOptions) || 'text' }
  }
  const type = /^(text|binary|dataurl|base64)$/.test(fileOptions.type) ? fileOptions.type : 'text'
  callback = callback || function () { }
  filePath = mfs.fullpath(filePath)

  if (/^https?:/.test(filePath)) {
    // Handle HTTP/HTTPS file paths
    handleHttpFile(filePath, fileOptions, type, platform, callback)
  } else if (Capacitor.isNativePlatform()) {
    // Handle Capacitor native platform
    handleCapacitorFile(filePath, fileOptions, type, callback)
  } else if (nfs) {
    // Handle Node.js or NW.js
    handleNfsFile(filePath, fileOptions, type, callback)
  } else {
    // Fallback
    callback('msysFS: cannot read file', null)
  }
}

function handleHttpFile(filePath, fileOptions, type, platform, callback) {
  if (/nwjs|node/.test(platform)) {
    // Node.js/NW.js environment
    handleNodeHttpRequest(filePath, fileOptions, type, platform, callback)
  } else {
    // Browser environment (Cordova, browser)
    handleBrowserHttpRequest(filePath, fileOptions, type, callback)
  }
}

// Browser/Cordova HTTP request handling
function handleBrowserHttpRequest(filePath, fileOptions, type, callback) {
  const req = new XMLHttpRequest()

  req.onreadystatechange = function () {
    if (req.readyState !== XMLHttpRequest.DONE) return

    if (req.status === 200) {
      const contentType = req.getResponseHeader('content-type') || 'application/octet-stream'
      const response = processBrowserData(req, type, contentType)
      callback(null, response)
    } else {
      callback(req.status)
    }
  }

  req.open('GET', filePath)
  req.responseType = (type === 'binary' || type === 'dataurl') ? 'arraybuffer' : 'text'
  req.send()
}

function processBrowserData(req, type, contentType) {
  if (type === 'binary') {
    return req.response // ArrayBuffer for binary data
  } else if (type === 'dataurl') {
    const bytes = new Uint8Array(req.response)
    const binary = [].map.call(bytes, byte => String.fromCharCode(byte)).join('')
    const b64 = btoa(binary)
    return `data:${contentType};base64,${b64}`
  } else {
    return req.responseText // Default to text
  }
}

// HTTP/HTTPS file handling
function handleNodeHttpRequest(filePath, fileOptions, type, platform, callback, redirectCount = 0) {
  let timeouter

  timeouter = setTimeout(() => {
    timeouter = null
    abortRequest(request, callback)
  }, HTTP_TIMEOUT)

  const { options, postData } = prepareHttpRequest(filePath, fileOptions, platform)
  const request = (options.protocol === 'https:' ? https : http).request(options, (res) => {
    handleNodeHttpResponse(res, type, filePath, fileOptions, callback, timeouter, redirectCount)
  })

  request.on('error', (err) => {
    clearTimeout(timeouter)
    callback(err.message)
  })

  if (postData) request.write(postData)
  request.end()
}

function abortRequest(request, callback) {
  try { if (request) request.abort() } catch (e) { }
  callback('timeout')
}

function prepareHttpRequest(filePath, fileOptions, platform) {
  const digest = /(.*):DIGEST=(.+):(.*)$/.exec(filePath)
  const url = digest ? digest[1] : filePath
  const parsedUrl = parseUrl(url)

  const options = {
    protocol: parsedUrl.protocol,
    method: fileOptions.method || 'GET',
    host: parsedUrl.host,
    port: parsedUrl.port,
    path: parsedUrl.uri,
    headers: {},
  }

  let postData = null
  if (_.isObjectLike(fileOptions.http_options)) {
    ({ postData } = extractHttpOptions(fileOptions, options))
  }

  if (/nwjs|node/.test(platform)) {
    options.headers['User-Agent'] = 'Mosy mPatrol 1.0'
  }
  if (postData) {
    options.headers['Content-Length'] = postData.length
  }
  if (digest) {
    addDigestAuthorization(digest, parsedUrl, options, fileOptions)
  }

  if (fileOptions.mtime > 0) {
    options.headers['If-Modified-Since'] = new Date(fileOptions.mtime * 1000).toUTCString()
  }

  return { options, postData }
}

function parseUrl(url) {
  const parsed = url.match(/^(https?:)\/\/(?:([^:]+):([^@]*)@)?(([^/:]+)(?::(\d+))?)(.*)/)
  if (!parsed) throw new Error('Invalid URL')
  const [, protocol, username, password, hostport, host, port, uri] = parsed
  return { protocol, username, password, hostport, host, port, uri }
}

function extractHttpOptions(fileOptions, options) {
  let postData = null
  for (const key in fileOptions.http_options) {
    if (key === 'body') {
      postData = fileOptions.http_options[key]
    } else {
      options[key] = fileOptions.http_options[key]
    }
  }
  return { postData }
}

function addDigestAuthorization(digest, parsedUrl, options, fileOptions) {
  const [username, password, realm, nonce] = [parsedUrl.username, parsedUrl.password, digest[2], digest[3]]
  const ha1 = mcrypt.md5(`${username}:${realm}:${password}`)
  const ha2 = mcrypt.md5(`${options.method}:${parsedUrl.uri}`)
  const response = mcrypt.md5(`${ha1}:${nonce}:${ha2}`)
  options.headers.Authorization = `Digest username="${username}", realm="${realm}", nonce="${nonce}", uri="${parsedUrl.uri}", response="${response}"`
}

function resolveRedirectUrl(base, location) {
  try {
    return new URL(location, base).toString()
  } catch (e) {
    return location
  }
}

function handleNodeHttpResponse(res, type, filePath, fileOptions, callback, timeouter, redirectCount) {
  let finished = false
  const status = res.statusCode

  // Redirect handling
  if(status >= 300 && status < 400 && res.headers.location) {
    clearTimeout(timeouter)
    if(redirectCount >= 5) {
      return callback('too many redirects')
    }

    const newUrl = resolveRedirectUrl(filePath, res.headers.location)
    return handleNodeHttpRequest(
      newUrl,
      fileOptions,
      type,
      'node',
      callback,
      redirectCount + 1,
    )
  }

  const chunks = []

  res.on('data', (chunk) => chunks.push(chunk))

  res.on('end', () => {
    if (finished) return
    finished = true
    clearTimeout(timeouter)

    if (res.statusCode === 304) return callback()
    if (res.statusCode === 401 && typeof filePath === 'string') {
      return handleUnauthorized(res, filePath, fileOptions, callback)
    }
    if (res.statusCode !== 200) {
      return callback(res.statusCode)
    }

    const contentType = res.headers['content-type'] || 'application/octet-stream'
    const data = Buffer.concat(chunks)
    const lastModified = parseInt(new Date(res.headers['last-modified']).valueOf() / 1000 || Date.now() / 1000)

    if (fileOptions.mtime > 0 && lastModified > 0 && fileOptions.mtime > lastModified) {
      return callback()
    }

    const result = processData(data, type, contentType)
    callback(null, result, lastModified)
  })

  res.on('close', () => {
    if (finished) return
    finished = true
    clearTimeout(timeouter)
    callback('CONNECTION CLOSED')
  })

  res.on('error', (err) => {
    if (finished) return
    finished = true
    clearTimeout(timeouter)
    callback(err.message)
  })
}

function handleUnauthorized(res, filePath, fileOptions, callback) {
  const digest = /(.*):DIGEST=(.+):(.*)$/.exec(filePath)

  if (digest) {
    // If there's already a digest in the file path, we've likely retried already, so fail
    return callback(res.statusCode) // Perhaps invalid password
  }

  const auth = /realm="([^"]*)",.*nonce="([^"]*)"/.exec(res.headers['www-authenticate'])
  if (auth) {
    // Retry with digest authentication
    const newFilePath = `${filePath}:DIGEST=${auth[1]}:${auth[2]}`
    return mfs.readFile(newFilePath, fileOptions, callback)
  } else {
    return callback(res.statusCode) // No authentication challenge found
  }
}

function processData(data, type, contentType) {
  if (type === 'binary') return data
  if (type === 'dataurl') return `data:${contentType};base64,${data.toString('base64')}`
  return data.toString('utf8')
}

// Capacitor native platform file handling
async function handleCapacitorFile(filePath, fileOptions, type, callback) {
  const opt = prepareCapacitorOptions(filePath, type)
  try {
    let { data } = await Filesystem.readFile(opt)
    data = processCapacitorData(data, filePath, type)
    callback(null, data)
  } catch (e) {
    callback(String(e))
  }
}

function prepareCapacitorOptions(filePath, type) {
  const directory = /:\/\//.test(filePath) ? { path: filePath } : /^\//.test(filePath) ? { path: filePath, directory: Directory.Documents } : { path: filePath, directory: Directory.Data }
  if (type === 'text') directory.encoding = Encoding.UTF8
  return directory
}

function processCapacitorData(data, filePath, type) {
  if (type === 'binary') return Uint8Array.from(atob(data), c => c.charCodeAt(0))
  if (type === 'dataurl') return `data:${ext2mime[filePath.replace(/.*\./, '')] || 'application/octet-stream'};base64,${data}`
  return data
}

// NFS file handling (Node.js, NW.js)
function handleNfsFile(filePath, fileOptions, type, callback) {
  const options = getNfsOptions(type)
  const mimeType = getNfsMimeType(filePath, type)
  nfs.readFile(filePath, options, (err, data) => {
    if (err) callback(err.message, null)
    else callback(null, type === 'dataurl' ? `data:${mimeType};base64,${data}` : data)
  })
}

function getNfsOptions(type) {
  return type === 'binary' ? {} : { encoding: type === 'base64' || type === 'dataurl' ? 'base64' : 'utf8' }
}

function getNfsMimeType(filePath, type) {
  return type === 'dataurl' ? ext2mime[filePath.replace(/.*\./, '')] || 'application/octet-stream' : ''
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Read and parse JSON file.
* Upon completion, calls provided callback. On error, first param contains error. On success, first param is null and second contains parsed JavaScript object.
* @param {string} file_path
* @param {function} callback(err, js_object) - called when finished;
*/
mfs.readFileJSON = function (file_path, callback) {
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.readFileJSON: invalid callback')
    return
  }
  mfs.readFile(file_path, 'text', function (err, text) {
    let json = null
    if(!err) {
      try { json = JSON.parse(text) } catch (e) { err = e }
    }
    callback(err, json)
  })
} // readFileJSON

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Read configuration for Mosy application
* @param {string} callback(cfg) - callback called after completeion with the actual configuration
*/
mfs.readConfiguration = function (callback) {
  let server_cfg = mfs.getItem('server.cfg')
  try{
    server_cfg = JSON.parse(server_cfg)
    if(typeof server_cfg !== 'object' || Array.isArray(server_cfg)) server_cfg = null
  }catch(e) {
    console.error('Invalid server.cfg: '+e)
    server_cfg = null
  }

  let local_cfg = mfs.getItem('local.cfg')
  try{
    local_cfg = JSON.parse(local_cfg)
    if(typeof local_cfg !== 'object' || Array.isArray(local_cfg)) local_cfg = null
  }catch(e) {
    console.error('Invalid local.cfg: '+e)
    local_cfg = null
  }

  let devel_cfg = mfs.getItem('devel.cfg')
  try{
    devel_cfg = JSON.parse(devel_cfg)
    if(typeof devel_cfg !== 'object' || Array.isArray(devel_cfg)) devel_cfg = null
  }catch(e) {
    console.error('Invalid devel.cfg: '+e)
    devel_cfg = null
  }

  let file_cfg = (!local_cfg && !devel_cfg) ? server_cfg : _.merge({}, server_cfg, local_cfg, devel_cfg)
  if(!file_cfg || typeof file_cfg !== 'object' || Array.isArray(file_cfg)) file_cfg = {} // ensure cfg will be hash
  callback(null, { file_cfg, server_cfg, local_cfg, devel_cfg })
} // readConfiguration

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Reads content of several files
* @param {array} file_paths - logical (prefixed) paths (see prefixes in module description)
* @param {function} callback(results) - called back when finished reading, returning array of strings read
*/
mfs.readFiles = function (files, callback, _results) {
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.readFiles: invalid callback')
    return
  }
  files = Array.isArray(files) ? files : files == null ? [] : _.toArray(files)
  _results = Array.isArray(_results) ? _results : []
  if(files.length === 0) return callback(_results)
  mfs.readFile(files.shift(), function (err, data) {
    data = err ? null : data
    _results.push(data)
    mfs.readFiles(files, callback, _results)
  })
} // readFiles

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Writes content to the file; creates directories to file if missing
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {function} callback(err) - called back when finished writing
*/
mfs.writeFile = function (file_path, data, type, callback) {
  if(typeof (type) === 'function') {
    callback = type
    type = 'text'
  }
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.writeFile: invalid callback')
    return
  }
  if(/^https?:/.test(file_path)) {
    return callback('Method not supported')
  }
  const dir = mfs.dir(file_path)
  mfs.statFile(dir, (err) => {
    if(err) {
      mfs.mkpath(dir, () => {
        mfs.writeFileWithoutMkpath(file_path, data, type, callback)
      })
    }else {
      mfs.writeFileWithoutMkpath(file_path, data, type, callback)
    }
  })
} // writeFile

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Writes content to the file; creates directories to file if missing
* It firstly writes all data into the temporary file and when all data are written,
* repaces destination file with that. This ensures original file will never be replaced with partial data.
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {function} callback(err) - called back when finished writing
*/
mfs.writeFileAtomic = function (file_path, data, type, callback) {
  if(typeof (type) === 'function') {
    callback = type
    type = 'text'
  }
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.writeFileAtomic: invalid callback')
    return
  }
  const dir = mfs.dir(file_path)
  const full_file_path = mfs.fullpath(file_path)
  const tmp_ext = '._tmp_' + Date.now()
  const file_path_tmp = file_path + tmp_ext
  const full_file_path_tmp = full_file_path + tmp_ext

  const writeFcn = () => {
    mfs.writeFileWithoutMkpath(file_path_tmp, data, type, (err) => {
      if(err) {
        try { nfs.unlinkSync(full_file_path_tmp) } catch (e) { console.error(e) }
        callback(err)
      }else {
        nfs.rename(full_file_path_tmp, full_file_path, err => {
          if(err) {
            console.error(err)
            try { nfs.unlinkSync(full_file_path_tmp) } catch (e) { console.error(e) }
          }
          callback(err)
        })
      }
    })
  }

  mfs.statFile(dir, (err) => {
    if(err) {
      mfs.mkpath(dir, writeFcn)
    }else {
      writeFcn()
    }
  })
} // writeFileAtomic

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Writes content to the file
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {function} callback(err) - called back when finished writing
*/
mfs.writeFileWithoutMkpath = async function (file_path, data, type, callback) {
  if(typeof (type) === 'function') {
    callback = type
    type = 'text'
  }
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.writeFileWithoutMkpath: invalid callback')
    return
  }
  file_path = mfs.fullpath(file_path)

  if(/^https?:/.test(file_path)) {
    return callback('msysFS: Not supported')
  }else if(Capacitor.isNativePlatform()) {
    try {
      const opt =
        /:\/\//.test(file_path) ? { path: file_path } :
          /^\//.test(file_path) ? { path: file_path, directory: Directory.Documents } :
            { path: file_path, directory: Directory.Data }
      if(type === 'text') {
        opt.encoding = Encoding.UTF8
      }else if(type === 'binary') {
        data = btoa(String.fromCharCode(...new Uint8Array(data)))
      }else if(type === 'dataurl') {
        data = data.replace(/.*?;base64,/, '')
        data = btoa(String.fromCharCode(...new Uint8Array(data)))
      }
      opt.data = data
      await Filesystem.writeFile(opt)
      callback()
    } catch (e) {
      callback(String(e))
    }
  }else if(nfs) {
    if(typeof (data) !== 'object') {
      data = _.toString(data)
      if(type === 'dataurl') {
        data = data.replace(/^.*?,/, '')
        data = Buffer.from(data, 'base64')
      }
    }
    nfs.writeFile(
      file_path,
      data,
      function (err) {
        if(err) {
          callback(err.stack)
        }else {
          callback(null)
        }
      },
    )
  }else {
    callback('msysFS: cannot read file', null)
  }
} // writeFileWithoutMkpath

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Writes content to the file; creates directories to file if missing
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {function} callback(err) - called back when finished writing
*/
mfs.appendFile = function (file_path, data, callback) {
  const dir = mfs.dir(file_path)
  mfs.statFile(dir, (err) => {
    if(err) {
      mfs.mkpath(dir, () => {
        mfs.appendFileWithoutMkpath(file_path, data, callback)
      })
    }else {
      mfs.appendFileWithoutMkpath(file_path, data, callback)
    }
  })
} // appendFile

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Append content to the file
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {function} callback(err) - called back when finished writing
*/
mfs.appendFileWithoutMkpath = function (file_path, data, callback) {
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.appendFileWithoutMkpath: invalid callback')
    return
  }
  file_path = mfs.fullpath(file_path)

  const cfs = Capacitor.isNativePlatform() ? cordova.file : undefined
  if(/^https?:/.test(file_path)) {
    return callback('msysFS: Not supported')
  }else if(cfs) {
    return callback('msysFS: Not supported')
  }else if(nfs) {
    nfs.appendFile(
      file_path,
      data,
      { encoding: 'utf8' },
      function (err) {
        if(err) {
          callback(err.stack)
        }else {
          callback(null)
        }
      },
    )
  }else {
    callback('msysFS: cannot read file', null)
  }
} // appendFileWithoutMkpath

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* sets modification time of the file/directory
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {number} mtime - epoch time (in seconds)
*/
mfs.touchFile = function (file_path, mtime, callback) {
  if(typeof (mtime) === 'function') {
    callback = mtime
    mtime = 0
  }
  mtime = _.natural(mtime) || parseInt(Date.now() / 1000)
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.touchFile: invalid callback')
    return
  }
  file_path = mfs.fullpath(file_path)
  const cfs = Capacitor.isNativePlatform() ? cordova.file : undefined
  if(nfs) {
    nfs.utimes(file_path, mtime, mtime, callback)
  }else if(cfs) {
    callback('Platform not yet supported')
  }else {
    callback('Platform not supported')
  }
} // touchFile

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Writes JSON content to the file
* @param {string} file_path - logical (prefixed) path (see prefixes in module description)
* @param {object} json - JSON data
* @param {function} callback(err, data) - called back when finished reading
*/
mfs.writeFileJSON = function (file_path, json, callback) {
  try {
    const txt = JSON.stringify(json)
    mfs.writeFileAtomic(file_path, txt, callback)
  } catch (e) {
    callback(e)
  }
} // writeFileJSON

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Retrieves update for given file from server
* - verifies MD% for the downloaded file
* @param {string} local_path - logical (prefixed) path (see prefixes in module description)
* @param {string} server_url - URL where the file resides on server
* @param {function} callback(err, local_file, tmp_file) - called back when finished reading
*/
mfs.downloadUpdate = function (local_path, server_url, done) {
  const local_file = mfs.fullpath(local_path)
  if(!local_file) return done('Invalid local_path: ' + local_path + ' > ' + local_file)
  const local_dir = local_file.replace(/[^\\/]+$/, '')
  const tmp_file = local_file + '.update'
  mfs.readFile(server_url + '.md5', (err, server_md5) => {
    if(err || !server_md5) {
      console.error(err)
      return done('Cannot download MD5 for ' + server_url)
    }
    mcrypt.md5file(local_file, (local_md5) => {
      if(local_md5 === server_md5) {
        try { nfs.unlinkSync(tmp_file) } catch (e) { }
        return done()
      }
      mcrypt.md5file(tmp_file, (tmp_md5) => {
        if(tmp_md5 === server_md5) return done(null, local_file, tmp_file)
        try { nfs.unlinkSync(tmp_file) } catch (e) { }
        mfs.mkpathSync(local_dir)
        const output = nfs.createWriteStream(tmp_file)
          .on('error', (e) => { done(); done = () => { } })
          .on('finish', () => {
            if(err) {
              console.error(err)
              return
            }
            mcrypt.md5file(tmp_file, (tmp_md5_new) => {
              if(tmp_md5_new === server_md5) {
                done(null, local_file, tmp_file)
              }else {
                done('Invalid MD5 for downloaded ' + tmp_file)
              }
            })
          })
        const request = /^https:/.test(server_url) ? https : http
        request.get(server_url, (response) => response.pipe(output))
      })
    })
  })
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Delete given directories/files
* @param {string|array} path(s) - logical paths to delete (see prefixes in module description)
* @param {function} callback() - called when finished deletion
*/
mfs.delete = function (paths, callback) {
  if(!callback) callback = function () { }
  if(typeof callback !== 'function') {
    console.error('mfs.delete: invalid callback')
    return
  }
  paths = Array.isArray(paths) ? paths : [_.toString(paths)]
  if(paths.length === 0) return callback()

  const path = mfs.fullpath(paths.shift())
  const cfs = Capacitor.isNativePlatform() ? cordova.file : undefined
  if(nfs) {
    mfs._deleteNFS(path, () => mfs.delete(paths, callback))
  }else if(cfs) {
    mfs._deleteCFS(path, () => mfs.delete(paths, callback))
  }else {
    callback()
  }
} // delete

mfs.move = async function (src, dst, callback) {
  if(!callback) callback = function() { }
  if(typeof callback !== 'function') {
    console.error('mfs.move: invalid callback')
    return
  }
  src = mfs.fullpath(src)
  dst = mfs.fullpath(dst)
  if(nfs) {
    nfs.rename(src, dst, callback)
  }else if(cfs) {
    await cfs.rename({
      from: src,
      to: dst,
    })
  }else {
    console.error('Not implemented')
  }
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Delete given directories/files on Node filesystem
* @param {array} path - physical path to delete (see prefixes in module description)
*/
mfs._deleteNFS = function (path, callback) {
  try {
    const stat = nfs.statSync(path)
    if(stat.isDirectory()) {
      const cmd = mfs.os() === 'win' ? 'rmdir /q /s ' : 'rm -rf '
      child_process.exec(`${cmd} "${path}"`, callback)
    }else {
      nfs.unlink(path, callback)
    }
  } catch (e) {
    return callback()
  }
} // _deleteNFS

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* asynchronously creates directory tree if not exists - for Node/NWJS/Cordova only !!!
* @param {string} dir_path - logical (prefixed) path (see prefixes in module description)
* @param {function} callback(err) - called back when finished writing
*/
mfs.mkpath = async function (dir_path, callback) {
  callback = typeof (callback) === 'function' ? callback : () => { }
  const [root, rel_path] = mfs.splitpath(dir_path)
  const dirs = String(rel_path??'') === '' ? [] : rel_path.split('/')
  if(/^https?:/.test(dir_path)) {
    return callback('msysFS: Not supported')
  }else if(Capacitor.isNativePlatform()) {
    try {
      const opt =
        /:\/\//.test(dir_path) ? { path: dir_path } :
          /^\//.test(dir_path) ? { path: dir_path, directory: Directory.Documents } :
            { path: dir_path, directory: Directory.Data }
      opt.recursive = true
      await Filesystem.mkdir(opt)
      callback()
    } catch (e) {
      callback(String(e))
    }
  }else if(nfs) {
    mfs._mkPathNFS(root, dirs, callback)
  }else {
    return callback('msysFS: Not supported')
  }
} // mkpath

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* synchronously creates directory tree if not exists - for Node/NWJS only !!!
* @param {string} dir_path - logical (prefixed) path (see prefixes in module description)
*/
mfs.mkpathSync = function (dir_path) {
  const [root, rel_path] = mfs.splitpath(dir_path)
  const dirs = String(rel_path??'') === '' ? [] : rel_path.split('/')

  const cfs = Capacitor.isNativePlatform() ? cordova.file : undefined
  if(/^https?:/.test(root)) {
    return false
  }else if(cfs) {
    return false
  }else if(nfs) {
    return mfs._mkPathNFSSync(root, dirs)
  }else {
    return false
  }
} // mkpathSync

////////////////////////////////////////////////////////////////////////////////////////////////////
mfs._mkPathNFS = function (parent_dir, subdirs, callback) {
  const umask = process.umask()
  process.umask(0)
  nfs.mkdir(parent_dir, 0o777, (err) => {
    process.umask(umask)
    if(err && err.code !== 'EEXIST') return callback('msysFS: cannot create dir')
    if(!subdirs || subdirs.length === 0) return callback()
    const dir = subdirs.shift()
    if(String(dir??'') === '' && subdirs.length === 0) return callback()
    mfs._mkPathNFS(parent_dir + dir + '/', subdirs, callback)
  })
}

////////////////////////////////////////////////////////////////////////////////////////////////////
mfs._mkPathNFSSync = function (parent_dir, subdirs) {
  const umask = process.umask()
  process.umask(0)
  let dir = parent_dir
  try { mkpath.sync(dir) } catch (e) { console.log(e) }
  subdirs = Array.isArray(subdirs) ? subdirs : []
  while(subdirs.length > 0) {
    const subdir = subdirs.shift()
    dir += (/[\\/]$/.test(dir) ? '' : '/') + subdir
    try {
      nfs.mkdirSync(dir)
    } catch (e) {
      if(e.code!=='EEXIST') console.error(e)
    }
  }
  process.umask(umask)
} // _mkPathNFSSync

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Traverse folders and calls given method for each entry according to search criteria
* @param {string} root_path - logical root where to start
* @param {object} [params] - parameters for the search
* @param {string} [params.type] - if "dir" returns only directories, if "file" returns only files
* @param {object} [params.maxdepth] - maximum depth of recursion to subdirectories
* @param {object} [params.include] - regexp; if does not match full entry path, entry will not be evaluated
* @param {object} [params.exclude] - regexp; if matches full entry path, entry will not be evaluated
* @param {function} [params.exec(path,size,modified,is_dir)] - called on each item; is_dir is false if entry is file, true if directory
* @param {function} [params.finish(entries)] - called when all entries were processed
*/
mfs.find = function (dir, params) {
  params = _.isObjectLike(params) ? params : {}
  params.dir = params.type !== 'file'
  params.file = params.type !== 'dir'
  params.maxdepth = parseInt(params.maxdepth)
  params.maxdepth = params.maxdepth >= 0 ? params.maxdepth : 999
  params.mindepth = parseInt(params.mindepth)
  params.mindepth = params.mindepth >= 0 ? params.mindepth : 0
  params.include = _.toString(params.include)
  try { params.include = params.include === '' ? null : new RegExp(params.include) } catch (e) { console.log(e) }
  params.exclude = _.toString(params.exclude)
  try { params.exclude = params.exclude === '' ? null : new RegExp(params.exclude) } catch (e) { console.log(e) }
  if(typeof (params.exec) !== 'function') delete params.exec
  if(typeof (params.finish) !== 'function') delete params.finish

  dir = _.toString(dir).replace(/\\/g, '/').replace(/\/$/, '')
  let dir_prefix = mfs.fullpath('').replace(/\/?$/, '/')
  if(/^(\/|\w:)/.test(dir)) dir_prefix = ''
  const cfs = Capacitor.isNativePlatform() ? cordova.file : undefined
  const entries = []
  if(nfs) {
    mfs._findNFS(dir_prefix, dir, [''], params, entries)
  }else if(cfs) {
    mfs._findCFS(dir_prefix, dir, [''], params, entries)
  }else {
    if(params.finish) { params.finish(entries) }
  }
} // find

mfs._findNFS = function (dir_prefix, dir, todo, params, entries) {
  // nothing more to do
  if(todo.length === 0) {
    if(params.finish) params.finish(entries)
    return
  }

  // get first path to process
  const rel_path = todo.shift()
  const path = dir === '' || rel_path === '' ? dir + rel_path : dir + '/' + rel_path
  const real_path = dir_prefix + path
  const depth = rel_path === '' ? 0 : rel_path.split('/').length
  try {
    const stat = nfs.statSync(real_path)
    const is_dir = stat.isDirectory()
    const size = is_dir ? -1 : stat.size
    const modified = parseInt(stat.mtime.valueOf() / 1000) || 0

    let match = (is_dir && params.dir) || (!is_dir && params.file)
    match = match && (params.mindepth === 0 || depth >= params.mindepth)
    match = match && (!params.exclude || !params.exclude.test(path))
    match = match && (!params.include || params.include.test(path))
    if(match) {
      const entry = [
        path,
        size,
        modified,
      ]
      entries.push(entry)
      if(params.exec) params.exec(path, size, modified, is_dir)
    }

    if(is_dir && depth < params.maxdepth) {
      nfs.readdir(real_path, (err, files) => {
        files = err || !Array.isArray(files) ? [] : files
        const prefix = rel_path === '' ? '' : rel_path + '/'
        files = files.map(file => prefix + file).sort()
        todo = files.concat(todo)
        this._findNFS(dir_prefix, dir, todo, params, entries)
      })
    }else {
      this._findNFS(dir_prefix, dir, todo, params, entries)
    }
  } catch (e) {
    this._findNFS(dir_prefix, dir, todo, params, entries)
  }
} // _findNFS

mfs._findCFS = function (dir_prefix, dir, todo, params, entries) {
  // nothing more to do
  if(todo.length === 0) {
    if(params.finish) params.finish(entries)
    return
  }

  // get first path to process
  const rel_path = todo.shift()
  const path = dir === '' || rel_path === '' ? dir + rel_path : dir + '/' + rel_path
  const real_path = dir_prefix + path
  const depth = rel_path === '' ? 0 : rel_path.split('/').length

  const findNext = () => {
    this._findNFS(dir_prefix, dir, todo, params, entries)
  }

  const processEntry = (entry, meta) => {
    const is_dir = entry.isDirectory
    const size = is_dir ? -1 : _.natural(meta.size)
    const modified = _.natural(meta.modificationTime.valueOf() / 1000)

    let match = (is_dir && params.dir) || (!is_dir && params.file)
    match = match && (params.mindepth === 0 || depth >= params.mindepth)
    match = match && (!params.exclude || !params.exclude.test(path))
    match = match && (!params.include || params.include.test(path))
    if(match) {
      entries.push([path, size, modified])
      if(params.exec) params.exec(path, size, modified, is_dir)
    }

    if(is_dir && depth < params.maxdepth) {
      const dirReader = entry.createReader()
      dirReader.readEntries(entries => {
        const prefix = rel_path === '' ? '' : rel_path + '/'
        let files = []
        if(Array.isArray(entries)) {
          for (let i = 0; i < entries.length; i++) {
            files.push(prefix + entries[i].name)
          }
        }
        files = files.sort()
        todo = files.concat(todo)
        findNext()
      }, findNext)
    }else {
      findNext()
    }
  }

  window.resolveLocalFileSystemURL(real_path, entry => {
    entry.getMetadata(meta => processEntry(entry, meta), findNext)
  }, findNext)
} // _findCFS

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Stores value for given name in local storage. If value is null, removes item from local storage.
* @param {string} name - name of the item
* @param {string} [value] - value for the item
*/
mfs.setItem = function (name, value) {
  const local_item = MOSY_APP + MOSY_HOST + '-' + name
  const nfs_path = nfs ? mfs.fullpath(MOSY_APP + MOSY_HOST + '.store/' + local_item) : null
  try {
    if(typeof (value) === 'undefined' || value == null) {
      if(nfs_path) {
        try { nfs.unlinkSync(nfs_path) } catch (e) {
          // console.error(e)
        }
      }else if(typeof (localStorage) === 'object') {
        localStorage.removeItem(local_item)
      }
    }else {
      if(nfs_path) {
        const tmp_path = nfs_path + '.tmp'
        const umask = process.umask()
        process.umask(0)
        try { nfs.unlinkSync(tmp_path) } catch (e) {
          // console.error(e)
        }
        nfs.writeFileSync(tmp_path, String(value))
        nfs.renameSync(tmp_path, nfs_path)
        process.umask(umask)
      }else if(typeof (localStorage) === 'object') {
        localStorage.setItem(local_item, value)
      }
    }
  } catch (e) {
    console.error(e)
  }
} // setItem

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Retrieves value for given name from local storage
* @param {string} name - name of the item
*/
mfs.getItem = function (name) {
  if(nfs) {
    try {
      const item = mfs.fullpath(MOSY_APP + MOSY_HOST + '.store/' + MOSY_APP + MOSY_HOST + '-' + name)
      const result = nfs.readFileSync(item, { encoding: 'utf8' })
      return result
    } catch (e) {
      return null
    }
  }else if(typeof (localStorage) === 'object') {
    return localStorage.getItem(MOSY_APP + MOSY_HOST + '-' + name)
  }
} // getItem

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Stores object for given name in local storage. If value is null, removes item from local storage.
* @param {string} name - name of the item
* @param {string} [value] - value for the item
*/
mfs.setJSON = function (name, value) {
  mfs.setItem(name, typeof (value) === 'undefined' || value == null ? null : JSON.stringifySafe(value))
} // setJSON

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Retrieves value parsed as JSON object for given name from local storage
* @param {string} name - name of the item
*/
mfs.getJSON = function (name) {
  const text = mfs.getItem(name)
  try {
    const json = JSON.parse(text)
    return json
  } catch (e) {
    return null
  }
} // getJSON

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Validate provided dataURI if it contains valid JPEG image data
* @param {string} datauri - BASE64 encoded data URI
*/
mfs.validJPG = function (datauri) {
  return mfs._checkDataURITail(datauri, 'data:image/jpeg;base64,', [0xFF, 0xD9])
} // validJPG

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Validate provided dataURI if it contains valid JPEG image data
* @param {string} datauri - BASE64 encoded data URI
*/
mfs.validPNG = function (datauri) {
  return mfs._checkDataURITail(datauri, 'data:image/png;base64,', [0x00, 0x00, 0x00, 0x00, 0x49, 0x45, 0x4e, 0x44, 0xae, 0x42, 0x60, 0x82])
} // validPNG

mfs._checkDataURITail = function (datauri, prefix, tail) {
  try {
    const base64 = datauri.replace(prefix, '')
    if(mfs.platform() === 'node') {
      const bin = Buffer.from(base64, 'base64')
      for (let i = 1; i <= tail.length; i++) {
        if(tail[tail.length - i] !== bin[bin.length - i]) return false
      }
    }else {
      const bin = atob(base64)
      for (let i = 1; i <= tail.length; i++) {
        if(tail[tail.length - i] !== bin.charCodeAt(bin.length - i)) return false
      }
    }
    return true
  } catch (e) {
    return false
  }
} // _checkDataURITail

mfs.mimeType = function(data, type) {
  let hex = ''
  if(type === 'hex') {
    hex = String(data??'').replace(/\s*/g, '')
  }else {
    let binary
    if(type === 'base64') {
      const head = data.substring(0, 44)
      const binaryString = atob(head)
      const binary = new Uint8Array(32)
      for(let i = 0; i < 32; i++) {
        binary[i] = binaryString.charCodeAt(i)
      }
    }else {
      binary = new Uint8Array(data)
    }
    hex = Array.from(binary).map(b => b.toString(16).padStart(2, '0')).join('')
  }
  for(const magic in mime_magic) {
    try{
      const re = new RegExp('^'+magic, 'i')
      if(re.test(hex)) return mime_magic[magic]
    }catch(e) {
      // ignore
    }
  }
  return 'application/octet-stream'
} // mimeType

// promisify all msysFS methods
for (const fcn in mfs) {
  const manyArgs = false
  if(typeof mfs[fcn] === 'function') {
    mfs.promises[fcn] = (...args) => {
      return new Promise((resolve, reject) => {
        function callback(err, ...results) {
          if(err) return reject(err)
          else return resolve(manyArgs ? results : results[0])
        }
        args.push(callback)
        mfs[fcn].call(this, ...args)
      })
    }
  }
}

// Permission management variables
mfs.permissionQueue = []
mfs.isProcessingPermissions = false

// Method to request permissions
mfs.requestPermission = function(permissionFunc) {
  return new Promise((resolve, reject) => {
    mfs.permissionQueue.push({ permissionFunc, resolve, reject })
    mfs.processPermissionQueue()
  })
}

// Method to process the permission queue
mfs.processPermissionQueue = async function() {
  if (mfs.isProcessingPermissions || mfs.permissionQueue.length === 0) {
    return
  }

  mfs.isProcessingPermissions = true
  const { permissionFunc, resolve, reject } = mfs.permissionQueue.shift()

  try {
    const result = await permissionFunc()
    resolve(result)
  } catch (error) {
    reject(error)
  } finally {
    mfs.isProcessingPermissions = false
    mfs.processPermissionQueue()
  }
}

mfs.requestPermissions = async function() {
  await mfs.requestPermission(async () => {
    const permission = await Filesystem.checkPermissions()
    if(permission.publicStorage === 'granted') return
    await Filesystem.requestPermissions()
  })
} // requestPermissions

// ensure .store directory for Node-based apps
if(Capacitor.isNativePlatform()) {
  mfs.requestPermissions()
  mfs.init()
  mfs.getDeviceId().then(id=>console.log('Device ID:', id))
} else {
  mfs.init()
}
